import {
  HTTPClient as HTTPClientCore,
  IRequestConfig
} from 'modules/ScrShopNew/store/utils/HTTPSClient';
import { handleRequestError } from 'helpers/network';
import ApiErrors from 'old-store/utils/API/APIErrors';
import { CANCEL } from 'redux-saga';
import {
  IFetchProductsAction,
  IGetProductSuggestionAction
} from 'modules/ScrShopNew/store/slices/products/types';
import {
  ICreateAccountWithPasswordParams,
  IVerifyLoginCodeParams
} from 'modules/ScrShopNew/store/slices/shop/types';
import {
  IApplyCouponParams,
  ICapturePayment,
  IConvertToOrderAction,
  ICreatePaymentAction,
  IReFetchLatestPayment,
  IReFetchPayment
} from 'modules/ScrShopNew/store/slices/orders/types';
import {
  IAddProductToCartAction,
  IChangeCheckoutProductQuantityAction,
  IFetchCartAction,
  IRecalculatePricingAction,
  IRemoveCheckoutProductAction,
  IChangePasswordParams,
  IFetchActiveCartAction,
  ILoginAction,
  IUpdateCreateCustomer,
  IUpdateShoppingCartProductAction
} from 'modules/ScrShopNew/store/slices/checkout/types';
import {
  IPhotobookSpread,
  ISpreadImage,
  IProductDetails,
  PhotobookUpdateType
} from 'modules/ScrPhotobook/store/slices/photobook';
import { TEvent } from 'helpers/analytics';

class HTTPClient extends HTTPClientCore {
  /**
   * Overridden method adds CancelToken symbol, that allow redux-saga'
   * "takeLatest" function to cancel any requests automatically.
   */
  public makeRequest<T = any>(config: IRequestConfig): Promise<T> {
    const controller = new AbortController();

    const request: any = super.makeRequest({
      ...config,
      signal: controller.signal
    });

    request[CANCEL] = () => controller.abort();

    return request;
  }
}

const APIClient = new HTTPClient({
  baseURL: window.API_URL,
  withCredentials: true,
  transformResponse: (response) => {
    const { data } = response;
    const hasErrors = Array.isArray(data?.errors) && data.errors.length > 0;
    const errorCode = Number(data?.code);

    if (hasErrors || errorCode >= 400) {
      ApiErrors.checkOnApiError(data);
    }

    return data;
  },
  onCatchNetworkError: (error) => {
    throw handleRequestError(error);
  }
});

export const Api = {
  Site: {
    get: () => APIClient.get(`/api/collections/${window.SITE_ID}`),
    getManifest: () => APIClient.get(`/api/webapp-manifest/${window.SITE_ID}`),
    markNewAppModalShown: () =>
      APIClient.put(`/api/webapp-manifest/${window.SITE_ID}/new-app-modal-shown`)
  },
  Products: {
    get: (props: IFetchProductsAction) => APIClient.get('/api/collection/shop/products', props),
    getSuggestion: (props: IGetProductSuggestionAction) =>
      APIClient.get('/api/shopping-carts/product-suggestion', {
        ...props,
        excludeProducts: props.excludeProducts && JSON.stringify(props.excludeProducts)
      })
  },
  Cart: {
    addProduct: (props: IAddProductToCartAction) =>
      APIClient.post('/api/collection/shop/cart/product', props),
    fetchCart: (props: IFetchCartAction) => APIClient.get('/api/collection/shop/cart', props),
    claimCart: (props: IFetchCartAction) =>
      APIClient.post('/api/collection/endCustomer/claimShoppingCart', props),
    deleteProduct: (props: IRemoveCheckoutProductAction) =>
      APIClient.delete('/api/collection/shop/cart/product', props),
    changeProductQuantity: (props: IChangeCheckoutProductQuantityAction) =>
      APIClient.post('/api/collection/shop/cart/product/changeQuantity', props),
    updateShoppingCartProduct: (props: IUpdateShoppingCartProductAction) =>
      APIClient.patch('/api/collection/shop/cart/product', props),
    fetchActiveCart: (props: IFetchActiveCartAction) =>
      APIClient.get('/api/shopping-carts/active-cart', props),
    recalculatePricing: (props: IRecalculatePricingAction) =>
      APIClient.patch(`/api/shopping-carts/${props.cartId}/recalculate-pricing`)
  },
  Auth: {
    updateOrCreateCustomer: (props: IUpdateCreateCustomer) =>
      APIClient.post('/api/collection/endCustomer/upsert', props),
    updateEndCustomerCountry: (props: Pick<IUpdateCreateCustomer, '_country' | 'email'>) =>
      APIClient.post('/api/collection/endCustomer/upsert-country', props),
    // Sets the self-chosen comment handle on the caller's existing record. `displayName` is
    // deliberately NOT `name` — that one feeds selection emails and invoices. Email is only
    // written when the record has none, so a permanent account's address can't be overwritten.
    saveProfile: (props: { displayName: string; email?: string }) =>
      APIClient.post('/api/end-customers/profile', {
        collection_id: window.SITE_ID,
        ...props
      }),
    login: (props: ILoginAction) => APIClient.post('/api/end-customers/signin', props),
    checkPassword: (email: string, collectionId: string) =>
      APIClient.get('/api/end-customers/check-password', {
        email,
        collectionId,
        generatePasswordIfNotExists: true
      }),
    checkEmail: (email: string, collectionId: string) =>
      APIClient.get('/api/end-customers/check-email', {
        email,
        collectionId,
        generatePasswordIfNotExists: true
      }),
    resetPassword: (email: string | null, collectionId: string) =>
      APIClient.get('/api/end-customers/reset-password', {
        email,
        collectionId
      }),
    // `language` is used for the email and for an account created by the code
    startOtpLogin: (email: string, collectionId: string, language?: number) =>
      APIClient.post('/api/end-customers/otp/start', { email, collectionId, language }),
    // a code for an existing account, e.g. one that signs in with its password by default
    sendOtpLoginCode: (email: string, collectionId: string) =>
      APIClient.post('/api/end-customers/otp/send', { email, collectionId }),
    verifyLoginCode: (props: IVerifyLoginCodeParams) =>
      APIClient.post('/api/end-customers/otp/verify', props),
    signin: (props: any) => APIClient.post('/api/end-customers/signin', props),
    signup: (props: ICreateAccountWithPasswordParams) =>
      APIClient.post('/api/end-customers/signup', props),
    changePassword: (props: IChangePasswordParams) =>
      APIClient.post('/api/collection/endCustomer/changePassword', props),
    revokePrivacyCheckBox: () =>
      APIClient.post('/api/collection/endCustomer/revokeGtcAndPrivacyCheckbox', {
        collection_id: window.SITE_ID
      }),
    updateEndCustomer: () =>
      APIClient.get('/api/collection/endCustomer', {
        collection_id: window.SITE_ID
      })
  },
  TempCustomer: {
    createTempCustomer: () =>
      APIClient.post(`/api/end-customers/temp?collection_id=${window.SITE_ID}`),
    setSelection: (props: any) => APIClient.post('/api/collection/selection/current', props),
    createSelection: () =>
      APIClient.put('/api/collection/selection', { collection_id: window.SITE_ID }),
    createSelectionFromImages: (props: { collection_id: string; image_ids: string[] }) =>
      APIClient.post('/api/collection/selection', props),
    unsetSelection: (props: any) => APIClient.post('/api/collection/selection/unsetCurrent', props),
    deleteSelection: (selectionId: string) =>
      APIClient.delete(`/api/end-customers/selection/${selectionId}`),
    like: (props: any) => APIClient.put('/api/image/like', props),
    likeBulk: (props: any) => APIClient.put('/api/image/like-bulk', props),
    comment: (props: any) => APIClient.put('/api/image/comment', props)
  },
  Comments: {
    // Every image's count in one call — the tiles badge themselves before any thread is opened.
    getCounts: () =>
      APIClient.get('/api/collection/comments/counts/for-end-customer', {
        collection_id: window.SITE_ID
      }),
    getForImage: (imageId: string) =>
      APIClient.get('/api/collection/comments/for-end-customer', {
        collection_id: window.SITE_ID,
        image_id: imageId
      }),
    create: (props: { image_id: string; text: string }) =>
      APIClient.post('/api/collection/comments', { collection_id: window.SITE_ID, ...props }),
    remove: (commentId: string) =>
      APIClient.delete(`/api/collection/comments/${commentId}`, {
        collection_id: window.SITE_ID
      })
  },
  // The endCustomerToken cookie authenticates these; a query param also works but would put a
  // 360-day token in the access logs. Joi rejects unknown body keys, so send exactly these fields.
  Reactions: {
    getForEndCustomer: () =>
      APIClient.get('/api/collection/reactions/for-end-customer', {
        collection_id: window.SITE_ID
      }),
    create: (props: { image_id: string; slot: number }) =>
      APIClient.post('/api/collection/reactions', { collection_id: window.SITE_ID, ...props }),
    // Keyed on (image, slot), not an id: `for-end-customer` returns own reactions without one.
    remove: (props: { image_id: string; slot: number }) =>
      APIClient.delete('/api/collection/reactions', {
        collection_id: window.SITE_ID,
        ...props
      }),
    bulkCreate: (props: { image_ids: string[]; slot: number }) =>
      APIClient.post('/api/collection/reactions/bulk', {
        collection_id: window.SITE_ID,
        ...props
      })
  },
  Selection: {
    createSelection: (props: any, headers: any) =>
      APIClient.put('api/selection/createSelectionRequest', props, headers),
    saveSelection: (props: any) => APIClient.post('/api/collection/selection/save', props),
    updateSelection: (props: any) => APIClient.post('/api/collection/selection/update', props)
  },
  Orders: {
    convertToOrder: (props: IConvertToOrderAction) =>
      APIClient.post('/api/collection/shop/cart/convertToOrder', props),
    createPayment: (props: ICreatePaymentAction) =>
      APIClient.post('/api/collection/shop/order/createPayment', props),
    applyCoupon: (props: IApplyCouponParams) =>
      APIClient.post('/api/collection/shop/cart/applyCoupon', props),
    removeCoupon: (props: Pick<IApplyCouponParams, 'shoppingCart_id'>) =>
      APIClient.delete('/api/shopping-carts/coupon', props),
    reFetchPayment: (props: IReFetchPayment) =>
      APIClient.get('/api/collection/shop/payment/notCompletedPayment', props),
    reFetchLatestPayment: (props: IReFetchLatestPayment) =>
      APIClient.get('/api/collection/shop/payment/latestNotCompletedPayment', props),
    capturePayment: (props: ICapturePayment) => APIClient.post('/api/shop/capture-payment', props)
  },
  Collection: {
    fetchBannersData: (collectionID: string) =>
      APIClient.get(`/api/collections/${collectionID}/banners-data`),
    checkDownloadPin: (props: { pin: string; collection_id: string }) =>
      APIClient.post('/api/download/downloadPin/check', props)
  },
  Portfolios: {
    fetchPortfolio: () =>
      APIClient.get('/api/portfolios/end-customer/portfolio', { portfolioId: window.SITE_ID }),
    fetchPortfolioCollections: (
      collectionsSkip: number,
      collectionsLimit: number,
      searchQuery: string,
      sortBy: string
    ) =>
      APIClient.get('/api/portfolios/end-customer/portfolio-collections', {
        portfolioId: window.SITE_ID,
        collectionsSkip,
        collectionsLimit,
        searchQuery,
        sortBy
      }),
    verifyPassword: (password: string, redirectUrl: string) =>
      APIClient.post('/api/portfolios/verify-password', {
        portfolioId: window.SITE_ID,
        password,
        redirectUrl
      })
  },
  Photobook: {
    fetchPhotobooks: () => APIClient.get(`/api/photobook?collection_id=${window.SITE_ID}`),
    fetchPhotobook: (id: string) =>
      APIClient.get(`/api/photobook/${id}?collection_id=${window.SITE_ID}`),
    createPhotobook: (props: { title: string; slug: string; _product: string }) =>
      APIClient.post(`/api/photobook?collection_id=${window.SITE_ID}`, props),
    deletePhotobook: (id: string) =>
      APIClient.delete(`/api/photobook/${id}?collection_id=${window.SITE_ID}`),
    updatePhotobookTitle: (id: string, title: string, slug: string) =>
      APIClient.put(`/api/photobook/${id}?collection_id=${window.SITE_ID}`, {
        title,
        slug,
        updateType: PhotobookUpdateType.ChangeTitle
      }),
    updatePhotobookSpreads: (
      id: string,
      title: string,
      slug: string,
      spreads: IPhotobookSpread[],
      updateType: PhotobookUpdateType
    ) =>
      APIClient.put(`/api/photobook/${id}?collection_id=${window.SITE_ID}`, {
        title,
        slug,
        spreads,
        updateType
      }),
    fetchLayouts: (aspectRatioX?: number, aspectRatioY?: number, photobookId?: string) =>
      APIClient.get(
        `/api/photobook/layouts?aspectRatioX=${aspectRatioX || ''}&aspectRatioY=${
          aspectRatioY || ''
        }&photobookId=${photobookId || ''}`
      ),
    updateCover: (id: string, productDetails: { color: string }) =>
      APIClient.put(`/api/photobook/${id}?collection_id=${window.SITE_ID}`, {
        productDetails,
        updateType: PhotobookUpdateType.UpdateCover
      }),
    updateCoverText: (id: string, productDetails: Partial<IProductDetails>) =>
      APIClient.put(`/api/photobook/${id}?collection_id=${window.SITE_ID}`, {
        productDetails,
        updateType: PhotobookUpdateType.UpdateCoverText
      }),
    updateCoverImage: (id: string, productDetails: { coverImage: ISpreadImage }) =>
      APIClient.put(`/api/photobook/${id}?collection_id=${window.SITE_ID}`, {
        productDetails,
        updateType: PhotobookUpdateType.UpdateCoverImage
      }),
    getPhotobookFonts: () => APIClient.get('/api/photobook/fonts'),
    reuploadPhotobook: (
      id: string,
      title: string,
      slug: string,
      spreads: ISpreadImage[],
      productDetails: IProductDetails
    ) =>
      APIClient.put(`/api/photobook/${id}?collection_id=${window.SITE_ID}`, {
        productDetails,
        spreads,
        title,
        slug,
        updateType: PhotobookUpdateType.UpdateUndoRedoAction
      }),
    getPreviewPhotobook: (id: string) => APIClient.get(`/api/photobook/${id}/preview`),
    duplicatePhotobook: (id: string) =>
      APIClient.post(`/api/photobook/duplicate/${id}?collection_id=${window.SITE_ID}`)
  },
  Shop: {
    getGlobalShopSettingsForCollection: (collectionID: string) =>
      APIClient.get(`/api/shop/global-settings-for-collection?collectionId=${collectionID}`),
    getLaboratory: (labId: string) => APIClient.get(`/api/laboratories/${labId}`),
    paymentIsApproved: (orderId?: string) =>
      APIClient.get(
        `/api/shop/payment-is-approved${orderId ? `/${orderId}` : ''}?collectionId=${
          window.SITE_ID
        }`
      ),
    getRecommendations: () => APIClient.get(`/api/shop/recs/${window.SITE_ID}`),
    getBestsellers: () => APIClient.get(`/api/shop/recs/${window.SITE_ID}?bestsellers=true`)
  },
  Images: {
    hideImage: (imageId: string) =>
      APIClient.put(
        `/api/collections/${window.SITE_ID}/hide-image?imageId=${imageId}&collectionId=${window.SITE_ID}`
      ),
    unHideImage: (imageId: string) =>
      APIClient.put(
        `/api/collections/${window.SITE_ID}/unhide-image?imageId=${imageId}&collectionId=${window.SITE_ID}`
      ),
    getImages: () =>
      APIClient.get(
        `/api/images?shared=${window.location.pathname.includes('share')}&collection_id=${
          window.SITE_ID
        }`
      ),
    getPreviewImages: () =>
      APIClient.get(`/api/images?preview=true&collection_id=${window.SITE_ID}`),
    getCollectionIdentities: (id: string) =>
      APIClient.get(`/api/face-recog/collection/${id}/identity?skip=0&limit=1000`),
    getImageIdsByIdentities: (identitiesIds: string[]) =>
      APIClient.get(
        `/api/face-recog/collection/${window.SITE_ID}/identity/image-ids${identitiesIds.reduce(
          (acc, id) => `${acc}&identities[]=${id}`,
          '?'
        )}`
      )
  },
  Slideshow: {
    fetch: (id: string) => APIClient.get(`/api/slideshows/${id}`)
  },
  Templates: {
    getTempalteFonts: () => APIClient.get('/api/collection-templates/template-fonts')
  },
  Analytics: {
    trackEvent: async (event: TEvent, beacon?: boolean): Promise<void> => {
      if (beacon && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon('/api/analytics', JSON.stringify(event));
      } else {
        return APIClient.post('/api/analytics', event);
      }
    },
    trackEventBulk: async (events: TEvent[], beacon?: boolean): Promise<void> => {
      if (beacon && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon('/api/analytics/bulk', JSON.stringify({ events }));
      } else {
        return APIClient.post('/api/analytics/bulk', { events });
      }
    }
  },
  Countries: {
    get: () => APIClient.get('/api/countries')
  },
  Messages: {
    getForEndCustomer: () =>
      APIClient.get(`/api/message/for-end-customer?collectionId=${window.SITE_ID}`),
    trackMessageView: (id: string) =>
      APIClient.post(`/api/message/${id}/track-view?collectionId=${window.SITE_ID}`)
  },
  Banners: {
    getOrderDeadlineBanner: () =>
      APIClient.get('/api/shop/customizable-banners?type=order-deadline')
  }
};

export default Api;
